/**
 * Experience registry — THE place to say what each experience shows.
 *
 * An experience (Investor, Home buyer, and later HMO and Serviced
 * accommodation) changes which figures, tiles, filters, sorts and lot sections
 * appear. Components never test `mode === 'investor'`; they read the active
 * spec from `useExperience()` and render what it declares.
 *
 * Adding an experience = adding one entry here. It becomes selectable when
 * the backend lists it in `availableModes` (see useExperience in
 * experiences/useExperience.ts).
 */
import type { CardSort } from '../api/types'

export type ExperienceId = 'investor' | 'homeBuyer' | 'hmo' | 'sa'

export type TileKey = 'resale' | 'area' | 'rent' | 'family'
export type StripKind = 'models' | 'estimate' | 'none'
export type FilterKey =
  | 'q' | 'scope' | 'savedFilters' | 'location' | 'status' | 'source' | 'visited'
  | 'assetClasses' | 'dwellingTypes' | 'builtForms' | 'tenures' | 'saleMethods' | 'beds' | 'price'
  | 'potentialBand' | 'legalPack' | 'labels'
export type GlanceKey = 'guide' | 'gdv' | 'maxBid' | 'profit' | 'refurb' | 'sqm' | 'rent' | 'rooms'
export type SectionKey = 'overview' | 'diligence' | 'legal' | 'financial'
/** Diligence panels on the lot page, gated by the server's component keys too. */
export type DiligenceKey = 'areaPotential' | 'resale' | 'rental' | 'market' | 'localContext' | 'areaComposition' | 'energy' | 'comparables' | 'schools'

export interface ExperienceSpec {
  id: ExperienceId
  label: string
  short: string
  /** Backend mode key; HMO and SA are not served yet and stay hidden until they are. */
  modeKey: string
  /** Saved-filter presets are stored per experience under this key. */
  presetKey: 'investor' | 'homeBuyer'
  /** Which deal model(s) rate the cards: the user's saved pick (up to two) or one fixed model. */
  models: { kind: 'userPick'; max: number } | { kind: 'fixed'; logicalModelId: string }
  card: {
    tiles: TileKey[]
    strip: StripKind
    /** What the strip on the photograph says when a result exists. */
    photoStrip: 'bid' | 'price'
  }
  register: {
    filters: FilterKey[]
    sorts: CardSort[]
    minimumDeal: boolean
  }
  lot: {
    glance: GlanceKey[]
    sections: SectionKey[]
    diligence: DiligenceKey[]
  }
  /** Settings sections this experience adds (the shared assumptions form). */
  settings: Array<'homeBuyer' | 'investor'>
}

/** Stable identity of the shared Home buyer definition. Results remain personal. */
export const HOME_BUYER_MODEL_ID = '5d41994e-00be-4e80-95fd-c50ec41a8838'
/** How many models an experience may select to rate the cards at once (the server enforces the same cap). */
export const MAX_SELECTED_MODELS = 3
/** The investor default when the saved selection only held the Home buyer model. */
export const DEFAULT_INVESTOR_MODEL_ID = '370f437f-96fa-4754-86bd-f59ee80bf60f'

const ALL_FILTERS: FilterKey[] = ['q', 'scope', 'savedFilters', 'location', 'status', 'source', 'visited', 'assetClasses', 'dwellingTypes', 'builtForms', 'tenures', 'saleMethods', 'beds', 'price', 'potentialBand', 'legalPack', 'labels']
const ALL_DILIGENCE: DiligenceKey[] = ['areaPotential', 'resale', 'rental', 'market', 'localContext', 'areaComposition', 'energy', 'comparables', 'schools']

export const experiences: Record<ExperienceId, ExperienceSpec> = {
  investor: {
    id: 'investor', label: 'Investor', short: 'Investor', modeKey: 'investor', presetKey: 'investor',
    models: { kind: 'userPick', max: MAX_SELECTED_MODELS },
    card: { tiles: ['resale', 'area', 'rent', 'family'], strip: 'models', photoStrip: 'bid' },
    register: { filters: ALL_FILTERS, sorts: ['updated_desc', 'potential_desc', 'rental_potential_desc', 'price_asc', 'price_desc', 'auction_asc'], minimumDeal: true },
    lot: { glance: ['guide', 'gdv', 'maxBid', 'profit', 'refurb', 'sqm'], sections: ['overview', 'diligence', 'legal', 'financial'], diligence: ALL_DILIGENCE },
    settings: ['investor'],
  },
  homeBuyer: {
    id: 'homeBuyer', label: 'Home buyer', short: 'Home buyer', modeKey: 'homeBuyer', presetKey: 'homeBuyer',
    models: { kind: 'fixed', logicalModelId: HOME_BUYER_MODEL_ID },
    card: { tiles: ['area', 'family'], strip: 'estimate', photoStrip: 'price' },
    register: { filters: ALL_FILTERS.filter(key => key !== 'potentialBand'), sorts: ['updated_desc', 'price_asc', 'price_desc', 'auction_asc'], minimumDeal: true },
    lot: { glance: ['guide', 'gdv', 'maxBid', 'sqm'], sections: ['overview', 'diligence', 'legal', 'financial'], diligence: ['areaPotential', 'market', 'localContext', 'areaComposition', 'energy', 'comparables', 'schools'] },
    settings: ['homeBuyer'],
  },
  hmo: {
    id: 'hmo', label: 'HMO', short: 'HMO', modeKey: 'hmo', presetKey: 'investor',
    models: { kind: 'userPick', max: MAX_SELECTED_MODELS },
    card: { tiles: ['rent', 'area', 'family', 'resale'], strip: 'models', photoStrip: 'bid' },
    register: { filters: ALL_FILTERS, sorts: ['updated_desc', 'rental_potential_desc', 'price_asc', 'price_desc', 'auction_asc'], minimumDeal: true },
    lot: { glance: ['guide', 'rent', 'rooms', 'maxBid', 'profit', 'sqm'], sections: ['overview', 'diligence', 'legal', 'financial'], diligence: ALL_DILIGENCE },
    settings: ['investor'],
  },
  sa: {
    id: 'sa', label: 'Serviced accommodation', short: 'SA', modeKey: 'sa', presetKey: 'investor',
    models: { kind: 'userPick', max: MAX_SELECTED_MODELS },
    card: { tiles: ['rent', 'area', 'resale', 'family'], strip: 'models', photoStrip: 'bid' },
    register: { filters: ALL_FILTERS, sorts: ['updated_desc', 'rental_potential_desc', 'price_asc', 'price_desc', 'auction_asc'], minimumDeal: true },
    lot: { glance: ['guide', 'rent', 'maxBid', 'profit', 'refurb', 'sqm'], sections: ['overview', 'diligence', 'legal', 'financial'], diligence: ALL_DILIGENCE },
    settings: ['investor'],
  },
}

export const TILE_LABELS: Record<TileKey, string> = { resale: 'Resale', area: 'Area', rent: 'Rent', family: 'Family' }

export function specForMode(modeKey: string | null | undefined): ExperienceSpec {
  const found = Object.values(experiences).find(spec => spec.modeKey === modeKey)
  return found ?? experiences.investor
}
